import { useMemo } from "react";
import { attachmentsListParams } from "@/features/attachments/lib/list-query-params";
import type {
	AttachmentsAdapter,
	AttachmentsDeleteVars,
	AttachmentsEditVars,
	AttachmentsUploadVars,
} from "@/features/attachments/types";
import {
	useDeleteAssetFile,
	useEditAssetFile,
	useUploadAssetFile,
} from "@/services/asset/asset.mutations";
import { assetInstanceQueryOptions } from "@/services/asset/asset.options";

/**
 * Pure list query options for asset attachments (route loaders).
 */
export function createAssetAttachmentsListQueryOptions(args: {
	assetId: string;
	organizationId: string;
	start: number;
	count: number;
}) {
	return assetInstanceQueryOptions.files.list(
		args.assetId,
		args.start,
		args.count,
		attachmentsListParams(args.organizationId),
		{ organizationId: args.organizationId },
	);
}

/**
 * Asset-scoped attachments adapter (hooks + query option factories).
 */
export function useAssetAttachmentsAdapter(
	assetId: string,
): AttachmentsAdapter {
	const uploadAsset = useUploadAssetFile();
	const deleteAsset = useDeleteAssetFile();
	const editAsset = useEditAssetFile();

	return useMemo(
		(): AttachmentsAdapter => ({
			kind: "asset",
			entityId: assetId,
			listQueryOptions: ({ organizationId, start, count }) =>
				createAssetAttachmentsListQueryOptions({
					assetId,
					organizationId,
					start,
					count,
				}),
			contentQueryOptions: ({ organizationId, fileInfoId }) =>
				assetInstanceQueryOptions.files.content(assetId, fileInfoId, {
					organizationId,
				}),
			thumbnailQueryOptions: ({ organizationId, fileInfoId }) =>
				assetInstanceQueryOptions.files.thumbnail(assetId, fileInfoId, {
					organizationId,
				}),
			uploadMutation: {
				mutateAsync: (vars: AttachmentsUploadVars) =>
					uploadAsset.mutateAsync({
						assetId: vars.entityId,
						file: vars.file,
						organizationId: vars.organizationId,
						description: vars.description,
						entityAttachmentRequirementTagId:
							vars.entityAttachmentRequirementTagId,
						onUploadProgress: vars.onUploadProgress,
					}),
				reset: () => uploadAsset.reset(),
				isPending: uploadAsset.isPending,
				error: (uploadAsset.error as Error | null) ?? null,
			},
			deleteMutation: {
				mutateAsync: (vars: AttachmentsDeleteVars) =>
					deleteAsset.mutateAsync({
						assetId: vars.entityId,
						fileId: vars.fileId,
						organizationId: vars.organizationId,
					}),
				reset: () => deleteAsset.reset(),
				isPending: deleteAsset.isPending,
				error: (deleteAsset.error as Error | null) ?? null,
			},
			editMutation: {
				mutateAsync: (vars: AttachmentsEditVars) =>
					editAsset.mutateAsync({
						assetId: vars.entityId,
						fileId: vars.fileId,
						name: vars.name,
						description: vars.description,
						organizationId: vars.organizationId,
					}),
				reset: () => editAsset.reset(),
				isPending: editAsset.isPending,
				error: (editAsset.error as Error | null) ?? null,
			},
		}),
		[assetId, deleteAsset, editAsset, uploadAsset],
	);
}
